/**
 * Prompt-2 — Suspicious Punches tile (HR dashboard).
 * Fake-GPS attempts are BLOCKED at punch time; off-site punches are FLAGGED.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, X, MapPinOff, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../lib/api';

export default function SuspiciousPunchesTile() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    apiGet('/hr/attendance/suspicious-punches', { days: 7 }).then(setData).catch(() => {});
  }, []);

  if (!data) return null;
  const total = data.total_suspicious || 0;

  if (total === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-800 font-display font-bold" data-testid="hr-suspicious-tile-clean">
        <ShieldCheck size={16} /> ✓ No suspicious punch activity in the last {data.days} days
      </div>
    );
  }

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="w-full text-left px-4 py-3 bg-rose-50 border border-rose-300 rounded-sm hover:border-rose-400"
        data-testid="hr-suspicious-tile">
        <div className="flex items-center gap-2 text-rose-800 font-display font-bold text-sm">
          <ShieldAlert size={16} /> {total} suspicious punch{total === 1 ? '' : 'es'} · last {data.days} days
          <span className="ml-auto text-xs underline">Review →</span>
        </div>
        <div className="flex gap-4 mt-1 text-xs text-rose-700">
          <span><Smartphone size={11} className="inline mr-1 -mt-0.5" />{data.mock_attempts.count} fake GPS blocked</span>
          <span><MapPinOff size={11} className="inline mr-1 -mt-0.5" />{data.off_site_punches.count} off-site flagged</span>
        </div>
      </button>
      {open && <Drawer data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

function Drawer({ data, onClose }) {
  const [tab, setTab] = useState('mock');
  const navigate = useNavigate();
  const records = tab === 'mock' ? data.mock_attempts.records : data.off_site_punches.records;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto" data-testid="hr-suspicious-drawer">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="kpi-overline">Attendance Integrity</div>
            <h3 className="font-display font-bold text-lg">Suspicious Punches · {data.days} days</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="hr-suspicious-close"><X size={16} /></button>
        </div>
        <div className="px-5 pt-4 flex gap-2">
          <TabBtn active={tab === 'mock'} onClick={() => setTab('mock')} testid="hr-suspicious-tab-mock">
            Fake GPS (Blocked) · {data.mock_attempts.count}
          </TabBtn>
          <TabBtn active={tab === 'offsite'} onClick={() => setTab('offsite')} testid="hr-suspicious-tab-offsite">
            Off-Site (Flagged) · {data.off_site_punches.count}
          </TabBtn>
        </div>
        <div className="p-5">
          <table className="w-full dense-table text-xs">
            <thead><tr><th>Employee</th><th>Date</th><th>Time</th><th className="text-right">Distance</th><th></th></tr></thead>
            <tbody>
              {records.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-400">Nothing in this window</td></tr>}
              {records.map((r, i) => (
                <tr key={i}>
                  <td>
                    <div className="font-semibold">{r.emp_name || '—'}</div>
                    {r.emp_code && <div className="text-[10px] text-slate-400 font-mono">{r.emp_code}</div>}
                  </td>
                  <td>{r.date}</td>
                  <td>{r.check_in || '—'}</td>
                  <td className="text-right tabular-nums">{r.off_site_distance_m != null ? `${(r.off_site_distance_m / 1000).toFixed(1)} km` : '—'}</td>
                  <td>
                    {r.employee_id && (
                      <button onClick={() => navigate(`/app/hr/employees?focus=${r.employee_id}`)} className="text-[#1F7A6E] hover:underline">View Profile</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border rounded-sm ${active ? 'border-[#1F7A6E] bg-teal-50 text-[#1F7A6E]' : 'border-slate-300 text-slate-500'}`}>
    {children}
  </button>
);
